import clsx from "clsx";
import { FunctionComponent } from "react";

import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { sharedStyles } from "@voxmedia/duet-ui/shared-styles";

import * as styles from "./CoralAuthErrorState.css";

const CoralAuthErrorState: FunctionComponent = () => {
  let errorMsg: React.ReactElement | null = null;

  const { coralConfig } = useConfigContext();

  if (!coralConfig) {
    throw new Error("missing coral config");
  }

  const auth = useAuth();
  if (auth.userLoading || (!auth.viewer && !auth.user)) {
    return null;
  }
  const viewer = auth.viewer;
  const user = auth.user;

  if (viewer) {
    if (
      viewer.status === "BANNED" ||
      viewer.communityMembershipStatus === "BANNED"
    ) {
      errorMsg = (
        <>
          Your commenting account has been frozen, please contact{" "}
          <a href={`mailto:${coralConfig.moderationEmail}`}>
            {coralConfig.moderationEmail}
          </a>{" "}
          for more information.
        </>
      );
    } else if (viewer.email === null) {
      errorMsg = (
        <>
          You&apos;re signed in, but you must{" "}
          <a href={`/users/${encodeURIComponent(viewer.username)}/account`}>
            provide and verify your email address
          </a>{" "}
          before you can use the new commenting experience. If you have already
          provided and verified your email, please allow a couple of minutes for
          the changes to take effect and{" "}
          <button
            className={sharedStyles.text.singleLink}
            onClick={() => location.reload()}
          >
            reload the page
          </button>
          .
        </>
      );
    } else if (!viewer.emailVerified) {
      errorMsg = (
        <>
          You&apos;re signed in, but you must{" "}
          <a href="/resend_confirmation">verify your email address</a> before
          you can use the new commenting experience. If you have already
          verified your email, please allow a couple of minutes for the changes
          to take effect and{" "}
          <button
            className={sharedStyles.text.singleLink}
            onClick={() => location.reload()}
          >
            reload the page
          </button>
          .
        </>
      );
    }
  }
  if (user) {
    let errorAction;
    if (!user.emailVerified && !user.commentingName) {
      errorAction = "verify your email address and add a commenting name";
    } else if (!user.emailVerified) {
      errorAction = "verify your email address";
    } else if (!user.commentingName) {
      errorAction = "add a commenting name";
    }

    if (errorAction) {
      errorMsg = (
        <>
          You&apos;re signed in, but you must{" "}
          <a href="/account/profile">{errorAction}</a>. If you have already done
          so, try {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            role="button"
            href="#"
            className={sharedStyles.text.singleLink}
            onClick={() => location.reload()}
          >
            reloading the page
          </a>
          . Your first few comments may be reviewed by a site moderator.
        </>
      );
    }
    // Email is verified, but there is no coral token.
    // Something might have gone wrong with the commenting name
    // or some other bug. Direct user to account profile page, to
    // maybe get more information.
    else if (!user.coral) {
      errorMsg = (
        <>
          You&apos;re signed in, but it seems that you have an incomplete
          profile. Please check your{" "}
          <a href="/account/profile">account profile page</a>.
        </>
      );
    }
  }

  if (!errorMsg) {
    return null;
  }
  return (
    <div className={clsx(styles.errorContainer, sharedStyles.text.linkStyles)}>
      {errorMsg}
    </div>
  );
};

export default CoralAuthErrorState;
